/**
 * The one way an entry's logbook status is written, everywhere it appears.
 *
 * The flight list said "Needs confirming", the logbook grid said "Draft", and
 * the aircraft page said "In logbook" where the grid said "Approved" — four
 * words for two states, so a pilot moving between pages could reasonably think
 * they were looking at different things. One vocabulary, one badge.
 *
 * Sentence case with a small dot, not dashed uppercase: the badge used to be
 * the loudest thing on a flight card, louder than the registration.
 */

export type EntryStatus = 'none' | 'draft' | 'approved' | 'merged';

const ENTRY_STATUS_LABEL: Record<EntryStatus, string> = {
  none: 'No entry',
  draft: 'Draft',
  approved: 'Approved',
  merged: 'Merged',
};

const DOT: Record<EntryStatus, string> = {
  none: 'border-border-strong border bg-transparent',
  draft: 'bg-muted-foreground',
  approved: 'bg-accent',
  merged: 'border-border-strong border bg-transparent',
};

export function StatusBadge({
  status,
  label,
  title,
  ...rest
}: {
  status: EntryStatus;
  /** Overrides the standard label, for a status that isn't an entry's (e.g. "Partial coverage"). */
  label?: string;
  title?: string;
  'data-testid'?: string;
}) {
  return (
    <span
      className="text-muted-foreground inline-flex shrink-0 items-center gap-1.5 text-xs"
      title={title}
      {...rest}
    >
      <span aria-hidden="true" className={`size-1.5 rounded-full ${DOT[status]}`} />
      {label ?? ENTRY_STATUS_LABEL[status]}
    </span>
  );
}
